import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { Button } from '@/components/ui/Button';
import { ProgressIndicator } from '../CampaignCreation/ProgressIndicator';
import {
  DemandGenAdCreationStep,
  type DemandGenAdFormData,
} from './steps/DemandGenAdCreationStep';
import { DEFAULT_DEMAND_GEN_AD_FORM_DATA } from './steps/demandGenAdFormDefaults';
import { debug } from '@/utils/debug';
import { useAuthStore } from '@/store/authStore';
import type { Campaign } from '@/types/campaign';
import type { AppConfiguration } from '@/types/api';
import apiClient from '@/services/api/client';
import { useMaterializePendingTargeted } from '@/hooks/useMaterializePendingTargeted';
import { batchDeleteImages } from '@/services/api/media';
import type { PendingTargeted } from '@/types/media';

interface AdGroup {
  ad_group_id?: string;
  ads_group_id?: string;
  ad_group_name?: string;
  ads_group_name?: string;
}

interface CreateDemandGenAdWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  campaignId: string;
  campaignName: string;
  adGroups: AdGroup[];
}

interface FormErrors {
  adGroup?: string;
  // Multi-Asset errors
  headlines?: string;
  descriptions?: string;
  landscapeImages?: string;
  squareImages?: string;
  logoImages?: string;
  businessName?: string;
  finalUrl?: string;
  // Video errors
  videoIds?: string;
  longHeadlines?: string;
  // Carousel errors
  carouselCards?: string;
}

// API types for Demand Gen ad creation
interface CreateDemandGenAdRequest {
  ad_group_id: string;
  ad_type: 'MULTI_ASSET' | 'VIDEO' | 'CAROUSEL';
  // Common fields
  ad_name?: string;  // Ad name visible in Google Ads console
  business_name: string;
  call_to_action: string;
  final_url?: string;
  // Multi-Asset fields
  headlines?: string[];
  descriptions?: string[];
  landscape_image_ids?: string[];
  square_image_ids?: string[];
  portrait_image_ids?: string[];
  logo_image_ids?: string[];
  // Video fields
  video_ids?: string[];
  long_headlines?: string[];
  // Carousel fields
  carousel_cards?: Array<{
    headline: string;
    image_id: string;
    final_url: string;
  }>;
}

interface CreateDemandGenAdResponse {
  success: boolean;
  ad_id: string;
  resource_name: string;
  message: string;
}

const wizardSteps = [
  { label: 'Ad Group', description: '' },
  { label: 'Ad Details', description: '' },
  { label: 'Review', description: '' },
];

export const CreateDemandGenAdWizard: React.FC<CreateDemandGenAdWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaignId,
  campaignName,
  adGroups,
}) => {
  const queryClient = useQueryClient();
  const { memberUuid } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const materializePending = useMaterializePendingTargeted();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Selected ad group
  const [selectedAdGroupId, setSelectedAdGroupId] = useState<string>('');

  // Demand Gen ad form data
  const [formData, setFormData] = useState<DemandGenAdFormData>(DEFAULT_DEMAND_GEN_AD_FORM_DATA);

  const [errors, setErrors] = useState<FormErrors>({});

  // Get selected ad group details
  const selectedAdGroup = adGroups.find(
    (ag) => (ag.ad_group_id || ag.ads_group_id) === selectedAdGroupId
  );
  const selectedAdGroupName = selectedAdGroup?.ad_group_name || selectedAdGroup?.ads_group_name || '';

  // Reset form when wizard opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setErrors({});
      setSubmitError(null);
      setSelectedAdGroupId('');
      setFormData(DEFAULT_DEMAND_GEN_AD_FORM_DATA);
    }
  }, [isOpen]);

  // Validation function for each step
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      // Validate ad group selection
      if (!selectedAdGroupId) {
        newErrors.adGroup = 'Please select an ad group';
      }
    }

    if (step === 2) {
      // Common validation
      if (!formData.businessName.trim()) {
        newErrors.businessName = 'Business name is required';
      } else if (formData.businessName.length > 25) {
        newErrors.businessName = 'Business name must be 25 characters or less';
      }

      // Ad type specific validation
      if (formData.adType === 'MULTI_ASSET') {
        // Validate Multi-Asset ad fields
        if (formData.landscapePending.length === 0) {
          newErrors.landscapeImages = 'At least 1 landscape image is required';
        }
        if (formData.squarePending.length === 0) {
          newErrors.squareImages = 'At least 1 square image is required';
        }
        if (formData.logoPending.length === 0) {
          newErrors.logoImages = 'At least 1 logo image is required';
        }

        const nonEmptyHeadlines = formData.headlines.filter((h) => h.trim().length > 0);
        if (nonEmptyHeadlines.length < 1) {
          newErrors.headlines = 'At least 1 headline is required';
        } else if (formData.headlines.some((h) => h.trim().length > 0 && h.trim().length > 40)) {
          newErrors.headlines = 'Headlines must be 40 characters or less';
        }

        const nonEmptyDescriptions = formData.descriptions.filter((d) => d.trim().length > 0);
        if (nonEmptyDescriptions.length < 1) {
          newErrors.descriptions = 'At least 1 description is required';
        } else if (formData.descriptions.some((d) => d.trim().length > 0 && d.trim().length > 90)) {
          newErrors.descriptions = 'Descriptions must be 90 characters or less';
        }

        if (!formData.finalUrl.trim()) {
          newErrors.finalUrl = 'Final URL is required';
        } else if (!formData.finalUrl.startsWith('http://') && !formData.finalUrl.startsWith('https://')) {
          newErrors.finalUrl = 'Final URL must start with http:// or https://';
        }
      } else if (formData.adType === 'VIDEO') {
        // Validate Video ad fields
        const nonEmptyVideoIds = formData.videoIds.filter((v) => v.trim().length > 0);
        if (nonEmptyVideoIds.length < 1) {
          newErrors.videoIds = 'At least 1 YouTube video URL is required';
        }

        if (formData.logoPending.length === 0) {
          newErrors.logoImages = 'At least 1 logo image is required';
        }

        const nonEmptyHeadlines = formData.headlines.filter((h) => h.trim().length > 0);
        if (nonEmptyHeadlines.length < 1) {
          newErrors.headlines = 'At least 1 headline is required';
        }

        const nonEmptyLongHeadlines = formData.longHeadlines.filter((h) => h.trim().length > 0);
        if (nonEmptyLongHeadlines.length < 1) {
          newErrors.longHeadlines = 'At least 1 long headline is required';
        }

        const nonEmptyDescriptions = formData.descriptions.filter((d) => d.trim().length > 0);
        if (nonEmptyDescriptions.length < 1) {
          newErrors.descriptions = 'At least 1 description is required';
        }

        if (!formData.finalUrl.trim()) {
          newErrors.finalUrl = 'Final URL is required';
        } else if (!formData.finalUrl.startsWith('http://') && !formData.finalUrl.startsWith('https://')) {
          newErrors.finalUrl = 'Final URL must start with http:// or https://';
        }
      } else if (formData.adType === 'CAROUSEL') {
        // Validate Carousel ad fields
        const validCards = formData.carouselCards.filter(
          (card) => card.headline.trim() && card.pending && card.finalUrl.trim()
        );
        if (validCards.length < 2) {
          newErrors.carouselCards = 'At least 2 complete carousel cards are required';
        }

        // Check individual card validation
        const hasInvalidUrl = formData.carouselCards.some(
          (card) =>
            card.finalUrl.trim() &&
            !card.finalUrl.startsWith('http://') &&
            !card.finalUrl.startsWith('https://')
        );
        if (hasInvalidUrl) {
          newErrors.carouselCards = 'All card URLs must start with http:// or https://';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, wizardSteps.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step: number) => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Materialize pending crops just before submit. Each call writes one
      // Targeted MediaImage to GCS+DB. Done now (not at picker confirm) so
      // abandoned wizard runs leave zero orphans.
      const validCarouselCards = formData.carouselCards.filter(
        (card) => card.headline.trim() && card.pending && card.finalUrl.trim(),
      );
      const [
        landscapeMaterialized,
        squareMaterialized,
        portraitMaterialized,
        logoMaterialized,
        carouselMaterialized,
      ] = await Promise.all([
        materializePending(formData.landscapePending),
        materializePending(formData.squarePending),
        materializePending(formData.portraitPending),
        materializePending(formData.logoPending),
        materializePending(
          validCarouselCards
            .map((c) => c.pending)
            .filter((p): p is PendingTargeted => p !== null),
        ),
      ]);
      // `materializePending` cleans up partial failures within a bucket;
      // the inner try/catch below cleans up across all 5 buckets if the
      // Demand Gen create call fails.
      const allMaterializedIds = [
        ...landscapeMaterialized, ...squareMaterialized, ...portraitMaterialized,
        ...logoMaterialized, ...carouselMaterialized,
      ].map((t) => t.id);
      const cleanupMaterialized = async () => {
        if (allMaterializedIds.length === 0 || !memberUuid) return;
        try {
          await batchDeleteImages({
            member_uuid: memberUuid,
            image_ids: allMaterializedIds,
          });
        } catch (cleanupErr) {
          debug.error('CreateDemandGenAdWizard: cleanup of materialized Targeted ids failed', cleanupErr);
        }
      };

      try {
      // Build API request based on ad type
      const request: CreateDemandGenAdRequest = {
        ad_group_id: selectedAdGroupId,
        ad_type: formData.adType,
        ad_name: formData.adName?.trim() || undefined,
        business_name: formData.businessName.trim(),
        call_to_action: formData.callToAction,
      };

      if (formData.adType === 'MULTI_ASSET') {
        request.headlines = formData.headlines.filter((h) => h.trim().length > 0);
        request.descriptions = formData.descriptions.filter((d) => d.trim().length > 0);
        request.final_url = formData.finalUrl.trim();
        request.landscape_image_ids = landscapeMaterialized.map((t) => t.id);
        request.square_image_ids = squareMaterialized.map((t) => t.id);
        request.portrait_image_ids = portraitMaterialized.length > 0 ? portraitMaterialized.map((t) => t.id) : undefined;
        request.logo_image_ids = logoMaterialized.map((t) => t.id);
      } else if (formData.adType === 'VIDEO') {
        request.video_ids = formData.videoIds.filter((v) => v.trim().length > 0);
        request.headlines = formData.headlines.filter((h) => h.trim().length > 0);
        request.long_headlines = formData.longHeadlines.filter((h) => h.trim().length > 0);
        request.descriptions = formData.descriptions.filter((d) => d.trim().length > 0);
        request.final_url = formData.finalUrl.trim();
        request.logo_image_ids = logoMaterialized.map((t) => t.id);
      } else if (formData.adType === 'CAROUSEL') {
        request.carousel_cards = validCarouselCards.map((card, i) => ({
          headline: card.headline.trim(),
          image_id: carouselMaterialized[i].id,
          final_url: card.finalUrl.trim(),
        }));
      }

      debug.log('CreateDemandGenAdWizard: Submitting Demand Gen ad creation request', {
        request,
        campaignId,
        selectedAdGroupId,
      });

      // Call the API to create the Demand Gen ad
      const response = await apiClient.post<CreateDemandGenAdResponse>(
        `/campaigns/${campaignId}/ad-groups/${selectedAdGroupId}/ads/demand-gen`,
        request,
        { timeout: 180000 } // 3 minutes for image uploads
      );

      debug.log('CreateDemandGenAdWizard: Demand Gen ad created successfully', response.data);

      // Build optimistic ad data
      // Include all fields needed for display waterfall: ad_name | headline -> ad_name -> headline -> business_name -> Ad ID
      const optimisticAd = {
        ad_id: response.data.ad_id || `optimistic_${Date.now()}`,
        ad_type: `DEMAND_GEN_${formData.adType}_AD`,
        ad_name: formData.adName?.trim() || '',
        business_name: formData.businessName.trim(),
        status: 'ENABLED' as const,
        headlines: formData.headlines.filter((h) => h.trim()),
        descriptions: formData.descriptions.filter((d) => d.trim()),
        final_urls: formData.finalUrl ? [{ url: formData.finalUrl }] : [],
      };

      // Optimistically update the campaign-from-cache query
      queryClient.setQueryData<Campaign>(['campaign-from-cache', campaignId], (oldData) => {
        if (!oldData) {
          debug.log('CreateDemandGenAdWizard: No oldData for campaign-from-cache');
          return oldData;
        }
        debug.log('CreateDemandGenAdWizard: Updating campaign-from-cache', {
          campaignId,
          selectedAdGroupId,
          adGroupsCount: oldData.ads_group_list?.length,
        });
        const updated = {
          ...oldData,
          ads_group_list: oldData.ads_group_list?.map((ag) => {
            if (ag.ad_group_id === selectedAdGroupId || ag.ads_group_id === selectedAdGroupId) {
              debug.log('CreateDemandGenAdWizard: Found matching ad group, adding ad');
              return {
                ...ag,
                ads: [...(ag.ads || []), optimisticAd],
                ads_list: [...(ag.ads_list || []), optimisticAd],
              };
            }
            return ag;
          }),
        } as Campaign;
        return updated;
      });

      // Also update app-config cache
      if (memberUuid) {
        queryClient.setQueryData<AppConfiguration>(['app-config', memberUuid], (oldConfig) => {
          if (!oldConfig?.configuration?.campaign_list) {
            return oldConfig;
          }
          return {
            ...oldConfig,
            configuration: {
              ...oldConfig.configuration,
              campaign_list: oldConfig.configuration.campaign_list.map((camp) => {
                if (String(camp.campaign_id) === String(campaignId)) {
                  return {
                    ...camp,
                    ads_group_list: camp.ads_group_list?.map((ag) => {
                      if (ag.ad_group_id === selectedAdGroupId || ag.ads_group_id === selectedAdGroupId) {
                        return {
                          ...ag,
                          ads: [...(ag.ads || []), optimisticAd],
                          ads_list: [...(ag.ads_list || []), optimisticAd],
                        };
                      }
                      return ag;
                    }),
                  };
                }
                return camp;
              }),
            },
          };
        });
      }

      debug.log('CreateDemandGenAdWizard: Optimistically updated caches');

      // Invalidate campaign-from-cache to trigger a re-derive
      queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });

      await onSuccess();
      onClose();
      } catch (submitErr) {
        await cleanupMaterialized();
        throw submitErr;
      }
    } catch (error: unknown) {
      debug.error('CreateDemandGenAdWizard: Failed to create Demand Gen ad', error);

      let errorMessage = 'Failed to create Demand Gen ad. Please try again.';

      const apiError = error as {
        response?: { data?: { detail?: string; message?: string; error?: string } };
        message?: string;
      };
      if (apiError.response?.data) {
        const data = apiError.response.data;
        errorMessage = data.detail || data.message || data.error || errorMessage;

        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get ad type label for display
  const getAdTypeLabel = (adType: string): string => {
    switch (adType) {
      case 'MULTI_ASSET':
        return 'Multi-Asset Ad';
      case 'VIDEO':
        return 'Video Ad';
      case 'CAROUSEL':
        return 'Carousel Ad';
      default:
        return 'Demand Gen Ad';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Create New Demand Gen Ad</h2>
          <p className="text-sm text-gray-600 mt-1">
            Add an ad to <span className="font-medium">{campaignName}</span>
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={wizardSteps.length}
            steps={wizardSteps}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Step Content */}
        <div
          ref={scrollBodyRef}
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {/* Step 1: Select Ad Group */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Ad Group</h3>
                <p className="text-sm text-gray-600">
                  Choose which ad group to add the new Demand Gen ad to.
                </p>
              </div>

              <div className="space-y-3">
                {adGroups.map((ag) => {
                  const agId = ag.ad_group_id || ag.ads_group_id || '';
                  const agName = ag.ad_group_name || ag.ads_group_name || 'Unnamed Ad Group';
                  const isSelected = selectedAdGroupId === agId;

                  return (
                    <label
                      key={agId}
                      className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="adGroup"
                        value={agId}
                        checked={isSelected}
                        onChange={(e) => setSelectedAdGroupId(e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">{agName}</span>
                    </label>
                  );
                })}
              </div>

              {errors.adGroup && <p className="text-sm text-red-600">{errors.adGroup}</p>}
            </div>
          )}

          {/* Step 2: Ad Creation Form */}
          {currentStep === 2 && (
            <DemandGenAdCreationStep
              formData={formData}
              onChangeFormData={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
              errors={{
                headlines: errors.headlines,
                descriptions: errors.descriptions,
                longHeadlines: errors.longHeadlines,
                landscapeImages: errors.landscapeImages,
                squareImages: errors.squareImages,
                logoImages: errors.logoImages,
                businessName: errors.businessName,
                finalUrl: errors.finalUrl,
                videoIds: errors.videoIds,
                carouselCards: errors.carouselCards,
              }}
            />
          )}

          {/* Step 3: Review */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Review Your Demand Gen Ad</h3>

              {/* Ad Group Info */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Target Ad Group</h4>
                <p className="text-sm text-gray-900">{selectedAdGroupName}</p>
              </div>

              {/* Ad Type */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Ad Type</h4>
                <p className="text-sm text-gray-900">{getAdTypeLabel(formData.adType)}</p>
              </div>

              {/* Business Details */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Business Details</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Business Name:</span>{' '}
                    <span className="text-gray-900">{formData.businessName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Call to Action:</span>{' '}
                    <span className="text-gray-900">{formData.callToAction.replace('_', ' ')}</span>
                  </div>
                  {formData.finalUrl && (
                    <div>
                      <span className="text-gray-500">Final URL:</span>{' '}
                      <span className="text-gray-900 break-all">{formData.finalUrl}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Multi-Asset Summary */}
              {formData.adType === 'MULTI_ASSET' && (
                <>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Images</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Landscape (1.91:1):</span>{' '}
                        <span className="text-gray-900">{formData.landscapePending.length} images</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Square (1:1):</span>{' '}
                        <span className="text-gray-900">{formData.squarePending.length} images</span>
                      </div>
                      {formData.portraitPending.length > 0 && (
                        <div>
                          <span className="text-gray-500">Portrait (4:5):</span>{' '}
                          <span className="text-gray-900">{formData.portraitPending.length} images</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Logo:</span>{' '}
                        <span className="text-gray-900">{formData.logoPending.length} logos</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Text Assets</h4>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-gray-500">Headlines:</span>
                        <ul className="mt-1 list-disc list-inside text-gray-900">
                          {formData.headlines.filter((h) => h.trim()).map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="text-gray-500">Descriptions:</span>
                        <ul className="mt-1 list-disc list-inside text-gray-900">
                          {formData.descriptions.filter((d) => d.trim()).map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Video Summary */}
              {formData.adType === 'VIDEO' && (
                <>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Videos</h4>
                    <ul className="list-disc list-inside text-sm text-gray-900">
                      {formData.videoIds.filter((v) => v.trim()).map((v, i) => (
                        <li key={i} className="truncate">{v}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Text Assets</h4>
                    <div className="space-y-3 text-sm">
                      <div>
                        <span className="text-gray-500">Headlines:</span>
                        <ul className="mt-1 list-disc list-inside text-gray-900">
                          {formData.headlines.filter((h) => h.trim()).map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="text-gray-500">Long Headlines:</span>
                        <ul className="mt-1 list-disc list-inside text-gray-900">
                          {formData.longHeadlines.filter((h) => h.trim()).map((h, i) => (
                            <li key={i}>{h}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <span className="text-gray-500">Descriptions:</span>
                        <ul className="mt-1 list-disc list-inside text-gray-900">
                          {formData.descriptions.filter((d) => d.trim()).map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Carousel Summary */}
              {formData.adType === 'CAROUSEL' && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Carousel Cards</h4>
                  <div className="space-y-2 text-sm">
                    {formData.carouselCards
                      .filter((card) => card.headline.trim() || card.pending || card.finalUrl.trim())
                      .map((card, i) => (
                        <div key={i} className="p-2 bg-white border border-gray-100 rounded">
                          <div>
                            <span className="text-gray-500">Card {i + 1}:</span>{' '}
                            <span className="text-gray-900">{card.headline || '(no headline)'}</span>
                          </div>
                          <div className="text-xs text-gray-500 truncate">{card.finalUrl}</div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{submitError}</p>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Step {currentStep} of {wizardSteps.length}
            </div>

            <div className="flex space-x-3">
              {currentStep > 1 && (
                <Button variant="secondary" onClick={handleBack} disabled={isSubmitting}>
                  Back
                </Button>
              )}

              {currentStep < wizardSteps.length ? (
                <Button variant="primary" onClick={handleNext} disabled={isSubmitting}>
                  Next
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Demand Gen Ad'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
